import { createBrowserRouter } from 'react-router-dom'
import { lazy } from 'react'
import { Layout } from './components/layout'
import ProtectedRoute from './components/ProtectedRoute'
import DashboardLayout from './components/layout/DashboardLayout'
import { S } from './components/layout/LazyRoute'

// ── Lazy loading : chaque page ne se charge que quand l'utilisateur la visite ──

// Pages publiques
const Home         = lazy(() => import('./pages/Home'))
const About        = lazy(() => import('./pages/About'))
const Services     = lazy(() => import('./pages/Services'))
const Portfolio    = lazy(() => import('./pages/Portfolio'))
const InstrumentPage = lazy(() => import('./pages/InstrumentPage'))
const News           = lazy(() => import('./pages/News'))
const ArticlePage    = lazy(() => import('./pages/ArticlePage'))
const Contact        = lazy(() => import('./pages/Contact'))
const Login          = lazy(() => import('./pages/Login'))
const Recyclage      = lazy(() => import('./pages/Recyclage'))
const Legal          = lazy(() => import('./pages/Legal'))
const Privacy        = lazy(() => import('./pages/Privacy'))
const NotFound       = lazy(() => import('./pages/NotFound'))

// Pages dashboard (jamais visitées par les clients → gain maximum)
const Dashboard            = lazy(() => import('./pages/Dashboard'))
const GuitarsManagement    = lazy(() => import('./pages/dashboard/GuitarsManagement'))
const NewsManagement       = lazy(() => import('./pages/dashboard/NewsManagement'))
const UsersManagement      = lazy(() => import('./pages/dashboard/UsersManagement'))
const StockManagement      = lazy(() => import('./pages/dashboard/StockManagement'))
const AddEditStock         = lazy(() => import('./pages/dashboard/AddEditStock'))
const AddEditInstrument    = lazy(() => import('./pages/dashboard/AddEditInstrument'))
const AddEditNews          = lazy(() => import('./pages/dashboard/AddEditNews'))
const ClientsManagement    = lazy(() => import('./pages/dashboard/ClientsManagement'))
const AddEditClient        = lazy(() => import('./pages/dashboard/AddEditClient'))
const ClientDetail         = lazy(() => import('./pages/dashboard/ClientDetail'))
const DealsManagement      = lazy(() => import('./pages/dashboard/DealsManagement'))
const AddEditDeal          = lazy(() => import('./pages/dashboard/AddEditDeal'))
const DealDetail           = lazy(() => import('./pages/dashboard/DealDetail'))
const ProductionManagement = lazy(() => import('./pages/dashboard/ProductionManagement'))
const AddEditProduction    = lazy(() => import('./pages/dashboard/AddEditProduction'))
const ProductionDetail     = lazy(() => import('./pages/dashboard/ProductionDetail'))
const ResourcesManagement  = lazy(() => import('./pages/dashboard/ResourcesManagement'))
const TechnicalDocumentation = lazy(() => import('./pages/dashboard/TechnicalDocumentation'))
const CustomGuitarSteps    = lazy(() => import('./pages/dashboard/CustomGuitarSteps'))
const ReadmeView           = lazy(() => import('./pages/dashboard/ReadmeView'))
const GuitarLifecycleView  = lazy(() => import('./pages/dashboard/GuitarLifecycleView'))
const TicketingManagement  = lazy(() => import('./pages/dashboard/TicketingManagement'))
const PricingManagement    = lazy(() => import('./pages/dashboard/PricingManagement'))
const WoodCollection       = lazy(() => import('./pages/WoodCollection'))
const WoodManagement       = lazy(() => import('./pages/dashboard/WoodManagement'))
const AddEditWood          = lazy(() => import('./pages/dashboard/AddEditWood'))

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <S><Home /></S>,
      },
      {
        path: 'a-propos',
        element: <S><About /></S>,
      },
      {
        path: 'services',
        element: <S><Services /></S>,
      },
      {
        path: 'mescreations',
        element: <S><Portfolio /></S>,
      },
      {
        path: 'mescreations/:slug',
        element: <S><InstrumentPage /></S>,
      },
      {
        path: 'portfolio',
        loader: () => new Response(null, {
          status: 301,
          headers: { Location: '/mescreations' },
        }),
      },
      {
        path: 'portfolio/:slug',
        loader: ({ params }) => new Response(null, {
          status: 301,
          headers: { Location: `/mescreations/${params.slug}` },
        }),
      },

      // ─── Redirections 301 depuis l'ancien site Wix ───────────────────────────
      {
        path: 'electrique',
        loader: () => new Response(null, { status: 301, headers: { Location: '/mescreations?type=electrique' } }),
      },
      {
        path: 'acoustique',
        loader: () => new Response(null, { status: 301, headers: { Location: '/mescreations?type=acoustique' } }),
      },
      {
        path: 'reparation',
        loader: () => new Response(null, { status: 301, headers: { Location: '/services#reparation' } }),
      },
      {
        path: 'formation',
        loader: () => new Response(null, { status: 301, headers: { Location: '/services#formation' } }),
      },
      {
        path: 'recyclage',
        element: <S><Recyclage /></S>,
      },
      {
        path: 'bass-sirocco-008',
        loader: () => new Response(null, { status: 301, headers: { Location: '/mescreations/sirocco-4-fretless' } }),
      },
      {
        path: 'copie-de-bass-sirocco-4-008',
        loader: () => new Response(null, { status: 301, headers: { Location: '/mescreations/sirocco-5' } }),
      },
      {
        path: 'guitare-eole-001',
        loader: () => new Response(null, { status: 301, headers: { Location: '/mescreations/eole' } }),
      },
      {
        path: 'guitare-cymaster-013',
        loader: () => new Response(null, { status: 301, headers: { Location: '/mescreations/cymaster-013' } }),
      },
      {
        path: 'guitare-x-014',
        loader: () => new Response(null, { status: 301, headers: { Location: '/mescreations/x-by-cy' } }),
      },
      // ─────────────────────────────────────────────────────────────────────────

      {
        path: 'actualites',
        element: <S><News /></S>,
      },
      {
        path: 'actualites/:slug',
        element: <S><ArticlePage /></S>,
      },
      {
        path: 'ma-matiere-premiere',
        element: <S><WoodCollection /></S>,
      },
      {
        path: 'contact',
        element: <S><Contact /></S>,
      },
      {
        path: 'login',
        element: <S><Login /></S>,
      },
      {
        path: 'dashboard',
        element: <ProtectedRoute />,
        children: [
          {
            element: <DashboardLayout />,
            children: [
              { index: true,                          element: <S><Dashboard /></S> },
              { path: 'guitars',                      element: <S><GuitarsManagement /></S> },
              { path: 'guitars/add',                  element: <S><AddEditInstrument /></S> },
              { path: 'guitars/edit',                 element: <S><AddEditInstrument /></S> },
              { path: 'news',                         element: <S><NewsManagement /></S> },
              { path: 'news/add',                     element: <S><AddEditNews /></S> },
              { path: 'news/edit',                    element: <S><AddEditNews /></S> },
              { path: 'users',                        element: <S><UsersManagement /></S> },
              { path: 'clients',                      element: <S><ClientsManagement /></S> },
              { path: 'clients/add',                  element: <S><AddEditClient /></S> },
              { path: 'clients/edit',                 element: <S><AddEditClient /></S> },
              { path: 'clients/:id',                  element: <S><ClientDetail /></S> },
              { path: 'deals',                        element: <S><DealsManagement /></S> },
              { path: 'deals/add',                    element: <S><AddEditDeal /></S> },
              { path: 'deals/edit',                   element: <S><AddEditDeal /></S> },
              { path: 'deals/:id',                    element: <S><DealDetail /></S> },
              { path: 'production',                   element: <S><ProductionManagement /></S> },
              { path: 'production/add',               element: <S><AddEditProduction /></S> },
              { path: 'production/edit',              element: <S><AddEditProduction /></S> },
              { path: 'production/:id',               element: <S><ProductionDetail /></S> },
              { path: 'stock',                        element: <S><StockManagement /></S> },
              { path: 'stock/add',                    element: <S><AddEditStock /></S> },
              { path: 'stock/edit',                   element: <S><AddEditStock /></S> },
              { path: 'resources',                    element: <S><ResourcesManagement /></S> },
              { path: 'resources/technical-doc',      element: <S><TechnicalDocumentation /></S> },
              { path: 'resources/custom-guitar-steps',element: <S><CustomGuitarSteps /></S> },
              { path: 'resources/readme',             element: <S><ReadmeView /></S> },
              { path: 'resources/guitar-lifecycle',   element: <S><GuitarLifecycleView /></S> },
              { path: 'resources/ticketing',          element: <S><TicketingManagement /></S> },
              { path: 'pricing',                      element: <S><PricingManagement /></S> },
              { path: 'wood',                         element: <S><WoodManagement /></S> },
              { path: 'wood/add',                     element: <S><AddEditWood /></S> },
              { path: 'wood/edit',                    element: <S><AddEditWood /></S> },
            ],
          },
        ],
      },
      {
        path: 'mentions-legales',
        element: <S><Legal /></S>,
      },
      {
        path: 'politique-confidentialite',
        element: <S><Privacy /></S>,
      },
      {
        path: '*',
        element: <S><NotFound /></S>,
      },
    ],
  },
])
